
import React from 'react';
import { UserRoleBadge } from './UserRoleBadge';
import { deleteUserRole } from '../../../api/ApiUser';
import { message } from 'antd';
import { X } from 'lucide-react';
import { useTranslation } from '../../../../TranslationContext';

interface UserDetailsProps {
  user: any;
  onClose: () => void;
  companyId: string;
  applicationId: string;
  onRoleDeleted: () => void; // Callback to refresh data
}

export const UserDetails: React.FC<UserDetailsProps> = ({ 
  user, 
  onClose, 
  companyId, 
  applicationId,
  onRoleDeleted
}) => {
  const { t } = useTranslation();

  if (!user) return null;

  const handleDeleteRole = async (roleName: string) => {
    try {
      await deleteUserRole(companyId, applicationId, user.id, roleName);
      message.success(t('role_deleted_success'));
      onRoleDeleted(); // Refresh parent data
    } catch (error) {
      message.error(t('error_deleting_role'));
      console.error(t('error_deleting_role_log'), error);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-white rounded-xl shadow-sm border border-slate-100">
      <div className="col-span-2 flex justify-between items-center mb-2">
        <h3 className="text-lg font-medium text-slate-700">{t('user_details')}</h3>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-full hover:bg-slate-100"
          aria-label={t('close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <span className="text-sm font-medium text-slate-500">{t('email')}:</span>
          <p className="mt-1 text-sm text-slate-700">{user.email}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-slate-500">{t('full_name')}:</span>
          <p className="mt-1 text-sm text-slate-700">{user.fullName}</p>
        </div>
        <div>
          <span className="px-2.5 py-1 text-xs rounded-full font-medium">{t('status')}:</span>
          <p className="mt-1 text-sm text-slate-700">{user.status}</p>
        
          {/* <div className="mt-1">
            <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
              user.isLocked 
                ? 'bg-c06a70 bg-opacity-10 text-c06a70 border border-c06a70 border-opacity-20' 
                : 'bg-slate-100 text-slate-600 border border-slate-200'
            }`}>
              {t(`status_${user.isLocked ? 'locked' : 'active'}`)}
            </span>
          </div> */}
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <span className="text-sm font-medium text-slate-500">{t('roles')}:</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {user.roles?.map((role: string) => (
              <div key={role} className="flex items-center gap-1 group">
                <UserRoleBadge role={role} />
                <button
                  onClick={() => handleDeleteRole(role)}
                  className="ml-1 flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-c06a70 hover:text-white transition-all duration-200"
                  title={t('delete_role')}
                  aria-label={t('delete_role')}
                >
                  <span className="text-xs">×</span>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <span className="text-sm font-medium text-slate-500">{t('failureResponse')}:</span>
          <p className="mt-1 text-sm text-slate-700">{user.failureResponse|| t('not_available')}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-slate-500">{t('last_login')}:</span>
          <p className="mt-1 text-sm text-slate-700">{user.lastSuccessfull || t('not_available')}</p>
        </div>
      </div>
    </div>
  );
};
